import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import Layout from '../../../components/feature/Layout';
import { buildCanonicalHref } from '../../../seo/buildCanonicalHref';
import type { NewsArticle } from '../../../mocks/news';
import { getNewsById, getNewsList } from '../../../services/news';
import ArticleHeader from './components/ArticleHeader';
import ArticleContent from './components/ArticleContent';
import RelatedArticles from './components/RelatedArticles';
import { useTranslation } from 'react-i18next';

export default function NewsDetailPage() {
  const { t, i18n } = useTranslation();
  const { pathname } = useLocation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [relatedArticles, setRelatedArticles] = useState<NewsArticle[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    async function loadArticle() {
      if (!id) {
        navigate('/news', { replace: true });
        return;
      }

      setLoading(true);
      const result = await getNewsById(id, i18n.resolvedLanguage ?? i18n.language);
      if (!mounted) return;

      if (!result) {
        navigate('/news', { replace: true });
        return;
      }

      setArticle(result);
      const related = await getNewsList({ category: 'all', locale: i18n.resolvedLanguage ?? i18n.language });
      if (!mounted) return;
      setRelatedArticles(related.items);
      setLoading(false);
    }

    void loadArticle();
    return () => {
      mounted = false;
    };
  }, [id, navigate, i18n.resolvedLanguage, i18n.language]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [id]);

  if (loading) {
    return (
      <Layout>
        <section className="bg-white py-16">
          <div className="max-w-[1400px] mx-auto px-6 text-sm text-gray-400">加载文章中...</div>
        </section>
      </Layout>
    );
  }

  if (!article) return null;

  const brand = t('seo.brand');
  const documentTitle = `${article.title} · ${brand}`;
  const canonical = buildCanonicalHref(pathname);
  const keywordsContent = [brand, ...article.tags].filter(Boolean).join(', ');

  return (
    <Layout>
      <Helmet prioritizeSeoTags>
        <title>{documentTitle}</title>
        <meta name="description" content={article.summary} />
        {keywordsContent ? <meta name="keywords" content={keywordsContent} /> : null}
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.summary} />
        {canonical ? <meta property="og:url" content={canonical} /> : null}
        {article.image ? <meta property="og:image" content={article.image} /> : null}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.summary} />
        {article.image ? <meta name="twitter:image" content={article.image} /> : null}
      </Helmet>
      <ArticleHeader article={article} />
      <ArticleContent
        content={article.content}
        image={article.image}
        title={article.title}
      />
      <RelatedArticles currentId={article.id} articles={relatedArticles} />

      {/* Bottom CTA strip */}
      <section className="bg-dark-card border-t border-dark-border py-16">
        <div className="max-w-[1400px] mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <p className="text-sm text-slate-500 mb-1">{t('newsDetail.cta.subtitle')}</p>
            <p className="text-xl font-heading font-bold text-white">
              {t('newsDetail.cta.title')}
            </p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 px-7 py-3 bg-primary text-dark-bg font-semibold text-sm rounded-xl hover:bg-primary-light transition-colors duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-customer-service-line text-sm" />
              </div>
              {t('nav.cta')}
            </Link>
            <Link
              to="/news"
              className="inline-flex items-center gap-2 px-7 py-3 bg-dark-bg border border-dark-border text-slate-400 hover:text-white hover:border-primary/30 font-medium text-sm rounded-xl transition-all duration-200 cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-left-line text-sm" />
              </div>
              {t('newsDetail.backToList')}
            </Link>
          </div>
        </div>
      </section>
    </Layout>
  );
}
